import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { SuccessAccount } from '../../models';
import { SuccessAccount_Service } from '../../services';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, Subject } from 'rxjs';
import { switchMap, takeUntil, tap } from 'rxjs/operators';

export const matchesOtherControl = (other: AbstractControl | null, eq = (a: any, b: any) => a == b) => {
  return (control: AbstractControl): ValidationErrors | null => {
      if ((!other && control) 
          || (!control && other) 
          || (control && other && !eq(control.value, other.value))) {
          return { matchesOther: true };
      }
      return null;
  }
}

export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 30;
export const PASSWORD_PATTERN = new RegExp(`^(?![\\s])(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)[\\D\\d]{${PASSWORD_MIN_LENGTH},${PASSWORD_MAX_LENGTH}}(?<![\\s])$`);
export const PASSWORD_RULES = [
  Validators.required, 
  Validators.minLength(PASSWORD_MIN_LENGTH), 
  Validators.maxLength(PASSWORD_MAX_LENGTH), 
  Validators.pattern(PASSWORD_PATTERN)
];

export const PASSWORD_HINT = `
Password must be at least ${PASSWORD_MIN_LENGTH} characters long, less than ${PASSWORD_MAX_LENGTH} characters long,
and must contain, 1 lowercase letter, 1 uppercase letter, 1 number. It also cannot contain leading or trailing whitespace.
`;

export const randomPassword = () => {
  let letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  let numbers = "1234567890";
  let extra = "~!@#$%^&*()_+-=<>,.?/{}[]";
  let alphabet = (letters + letters.toLowerCase() + numbers + extra).split('');
  // console.debug('combos', Math.pow(alphabet.length, PASSWORD_MIN_LENGTH));
  let pass = '';
  let n = alphabet.length - 1;
  for (let i = 0; i < PASSWORD_MIN_LENGTH; i++) {
    pass += alphabet[Math.round(Math.random() * n)];
  }
  if (!PASSWORD_PATTERN.test(pass)) {
    let upper = letters[Math.round(Math.random() * (letters.length - 1))];
    let lower = letters[Math.round(Math.random() * (letters.length - 1))].toLowerCase();
    let num = numbers[Math.round(Math.random() * (numbers.length - 1))];
    pass = [upper, lower, num].reduce((acc, x) => {
      let pos = Math.round(Math.random() * (acc.length - 1));
      return acc.slice(0, pos) + x + acc.slice(pos, acc.length);
    }, pass);
  }
  return pass;
}

@Component({
  selector: 'lib-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss']
})
export class ChangePasswordComponent implements OnInit, OnDestroy {

  /**
   * The account that is getting the password reset
   */
  @Input()
  public user: SuccessAccount | undefined;
  @Input()
  public kind: 'admin' | 'owner' = 'admin';
  @Output()
  public onPasswordValueChange = new EventEmitter<string>();
  @Output()
  public onPasswordChange = new EventEmitter<string>();

  
  public passwordVisibility: 'password' | 'text' = 'password';
  PASSWORD_MIN_LENGTH = PASSWORD_MIN_LENGTH;
  PASSWORD_MAX_LENGTH = PASSWORD_MAX_LENGTH;
  PASSWORD_HINT = PASSWORD_HINT;
  form: FormGroup;

  private destroy$: Subject<void> = new Subject<void>();
  private resetPassword$: Subject<Observable<void>> = new Subject<Observable<void>>();

  constructor(
    private successAccountService: SuccessAccount_Service,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
  ) { 
    this.form = this.fb.group({
      password: ['', PASSWORD_RULES],
    })
  }

  ngOnInit(): void {
    if (this.kind == 'owner') {
      this.form.addControl('currentPassword', new FormControl('', [Validators.required]));
      this.form.addControl('confirmedPassword', new FormControl('', [Validators.required, matchesOtherControl(this.password)]));
    }
    // Called in doChangePassword
    this.resetPassword$.pipe(
      switchMap((observable) => observable), // successAccountService.resetPassword
      takeUntil(this.destroy$)
    ).subscribe();

    if (!this.isPasswordChange) {
      this.form.get('password')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: string) => {
        this.onPasswordValueChange.emit(value);
      });
    }
    
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get currentPassword() {
    return this.form.get('currentPassword');
  }

  get password() {
    return this.form.get('password');
  }

  get confirmedPassword() {
    return this.form.get('confirmedPassword');
  }

  get isPasswordChange() {
    return !!this.user?.uuid && (this.kind != 'admin');
  }

  doChangePassword(e: Event) {
    e.preventDefault();
    if (this.form.invalid || !this.user)
      return;

    let request = {
      password: this.form.get('password')?.value,
      currentPassword: this.form.get('currentPassword')?.value,
      successAccount_uuid: this.user.uuid
    }

    this.resetPassword$.next(
        this.successAccountService.resetPassword(request).pipe(
            tap({
                next: () => {
                    this.onPasswordChange.emit(request.password);
                    this.form.reset();
                    this.snackBar.open(`Password was Changed`, undefined, { duration: 4000, horizontalPosition: 'left', panelClass: 'snack-error' });
                },
                error: (err) => {
                    let message = "Error: " + err.error?.internalError || 'Something went wrong when attempting to reset the password';
                    this.snackBar.open(message, undefined, { duration: 4000, horizontalPosition: 'left', panelClass: 'snack-error' });
                    console.error(err);
                },
            }),
            takeUntil(this.destroy$)
        )
    );
  }

  suggestPassword(e: Event) {
    e.preventDefault();
    let pass = randomPassword();
    this.password?.setValue(pass);
    this.onPasswordValueChange.emit(pass);

    if (this.isPasswordChange && this.kind == 'owner') {
      this.confirmedPassword?.setValue(pass);
    }
  }

  togglePasswordVisibility() {
      this.passwordVisibility = this.passwordVisibility == 'password' ? 'text' : 'password';
  }

  get visibilityIcon() {
      return this.passwordVisibility == 'password' ? 'visibility_off' : 'visibility';
  }

  public updatePasswordValidity() {
    this.password?.updateValueAndValidity();
    this.confirmedPassword?.updateValueAndValidity()
  }
}
